import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import cx from "classnames";
import { Icon } from "../ui/icon";
import { Input } from "../ui/input";
import { fetchDocumentDetails, updateDocument } from "../../store/actions/documentActions";
import { convertNonAsciiToClosestAsciiEquivalent as convertToAscii } from "../../_helpers/utils";

import global from "../../scss/dhp.scss";
import { EditorPanelContext } from "../../containers/editor/EditorPanel";
import { EditorContext } from "../../containers/editor/EditorLayout";
import { Tooltip } from "reactstrap";
import useDocumentReader from "../../hooks/useDocumentReader";

let style = Object.assign({}, global);

const DocumentName = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { updateDocumentName } = useContext(EditorPanelContext);
  const { isDocumentSigned, widgets, updateWidgets } = useContext(EditorContext);

  const {
    documentDetails,
    documentDetails: {
      data: { document_name: documentName },
    },
    source,
  } = useSelector(state => state?.document);

  const [name, setName] = useState(documentName);
  const [editable, setEditable] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const innerRef = useRef(null);

  const { start: initDocumentReader } = useDocumentReader();

  const updateSmfsWithDocumentName = ({ documentName, updateDOM, updateContext }) => {
    // update all text widgets with system merge fied: document name
    const allSmfsWithDocumentName = [
      ...document.querySelectorAll(`.merge-field[data-mf-key="system.documents.document-name"]`),
    ];
    if (allSmfsWithDocumentName?.length > 0) {
      if (updateDOM) {
        allSmfsWithDocumentName.forEach(smf => {
          smf.innerText = documentName;
          smf.dataset.mfData = documentName;
          smf.classList.remove("empty");
          smf.classList.add("filled");
        });
      }

      if (updateContext) {
        const targetWidgetIds = [];
        allSmfsWithDocumentName.forEach(smf => {
          const widget = smf.closest(".dhp-root-widget");
          const widgetId = widget.id || widget.dataset.widgetId;
          if (!targetWidgetIds.includes(widgetId)) targetWidgetIds.push(widgetId);
        });

        updateWidgets(
          widgets.map(widget => {
            if (targetWidgetIds.includes(widget.id)) {
              const widgetNode = document.getElementById(widget.id);
              const widgetInner = widgetNode.querySelector(".dhp-widget-inner");
              const isGroupWidget = widgetNode.classList.contains("dhp-page-group");
              const widgetHeight = !isGroupWidget ? { height: widgetInner.offsetHeight } : {};

              return {
                ...widget,
                style: {
                  ...widget.style,
                  ...widgetHeight,
                },
                innerHTML: widgetNode.innerHTML,
              };
            } else {
              return widget;
            }
          })
        );
      }
    }
  };

  const onBlur = e => {
    const updatedName = convertToAscii(e.target.innerText.trim());
    if (updatedName !== "" && updatedName !== name) {
      setName(updatedName);
      updateSmfsWithDocumentName({ documentName: updatedName, updateDOM: true });
      dispatch(updateDocument(id, { document_name: updatedName }, false))
        .then(resp => {
          if (resp.status === 200) {
            updateSmfsWithDocumentName({ updateContext: true });
          }
        })
        .catch(e => {
          setName(documentName);
          updateSmfsWithDocumentName({ documentName: documentName, updateDOM: true });
          setEditable(false);
        });
    } else setEditable(false);
  };

  const onKeyDown = e => {
    if (([66, 73, 85].includes(e.which) && e.ctrlKey) || e.which === 13) {
      // disable ctrl+U, ctrl+B, ctrl+I and Enter
      e.preventDefault();
    }
  };

  const toggleTooltip = () =>
    setShowTooltip(!showTooltip && tooltipRef.current.scrollWidth < innerRef.current.scrollWidth + 20);

  useEffect(() => {
    if (name !== documentName) setName(documentName);
    setEditable(false);
  }, [documentName]);

  useEffect(() => {
    if (documentName !== name) updateDocumentName(name);
  }, [name]);

  useEffect(() => {
    if (source) return;
    if (documentDetails?.success) {
      if (documentDetails.data?.id !== id) {
        dispatch(fetchDocumentDetails(id));
      } else {
        initDocumentReader({ data: documentDetails.data });
      }
    }
  }, [documentDetails]);

  return (
    <>
      <h2
        className={cx(style["fw-7"], style["mb-0"], style["mr-5"], style["edit-title"], style["custom-tooltip"])}
        id="document-name" ref={tooltipRef}>
        {editable && (
          <Input
            type="contentEditable"
            returnType="noGroup"
            onBlur={e => onBlur(e)}
            onKeyDown={e => onKeyDown(e)}
            value={name}
            className={style["document-rename"]}
          />
        )}

        {!editable && (
          <>
            <span ref={innerRef}>{name}</span>

            {!isDocumentSigned && (
              <Link to="#" onClick={e => e.preventDefault()}>
                <Icon icon="ui-edit" onClick={() => setEditable(!editable)} />
              </Link>
            )}

            <Tooltip
              placement="bottom"
              isOpen={showTooltip}
              target={"document-name"}
              toggle={toggleTooltip}
              boundariesElement={document.getElementById("app")}>
              {name}
            </Tooltip>
          </>
        )}
      </h2>
    </>
  );
};

export default DocumentName;
